import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * BeforeYouJoinFAQ
 * ----------------
 * Compliance-friendly, no-hype FAQ block used on:
 *   - /waitlist  (public landing)
 *   - /waitlist-dashboard  (limited member back-office)
 *
 * Props:
 *   variant: 'dark' (landing page) | 'light' (dashboard)
 *   defaultOpen: index of question to open by default (-1 = all closed)
 */
const FAQS = [
  {
    q: 'What is BBC Quick List?',
    a: "BBC Quick List is an infrastructure-and-tools platform for builders who want a clean place to organize contacts, run lead capture, and grow a network of activated members. It is software plus a community workflow — not a job, salary, or income guarantee.",
  },
  {
    q: 'Is this a business opportunity?',
    a: "Members can earn from peer-to-peer compensation when they choose to participate in Pay-It-Forward and refer others, but joining the waiting list by itself is not an opportunity, employment, or investment. Whether you earn anything depends entirely on what you build and who you reach. Most people who casually try will earn nothing.",
  },
  {
    q: 'What is Pay-It-Forward?',
    a: "Pay-It-Forward is a small one-time member activation that creates a position in the matrix for a person who joined under you. It moves them from the waiting list to active. Members pay it forward with their own funds, on their own decision, and the funds go directly to another member in the system.",
  },
  {
    q: 'Am I buying leads?',
    a: "No. There are no purchased lead lists. Anyone in your Pay-It-Forward queue is there because they signed up through your referral link or rolled in through the normal queue mechanics. You are not buying contacts; you are activating positions.",
  },
  {
    q: 'Am I guaranteed income?',
    a: "No. There are no income guarantees, projected earnings, or promised payments. Anyone making such claims is doing so outside official communications. Most users build slowly. Some build nothing.",
  },
  {
    q: 'What happens after I join the waiting list?',
    a: "You receive a personal referral link, a limited back-office, and notifications when your sponsor activates you. You can share your referral link while you wait, but you do not have access to most member tools until you are activated.",
  },
  {
    q: 'Why do I need accurate contact information?',
    a: "Your email and mobile number are how you receive activation alerts, security alerts, queue movement updates, and important platform notifications. If your contact info is wrong, you may miss the moment you are activated and lose your spot in your sponsor's personal queue.",
  },
  {
    q: 'Who is this for?',
    a: "People who want to build a simple, modern, online business asset with their own effort. Members who like clean tools, plain rules, and a steady pace. People comfortable using a phone or laptop daily.",
  },
  {
    q: 'Who is this NOT for?',
    a: "It is not for people looking for fast money, push-button systems, or guaranteed passive income. It is not for people who refuse to read the rules, share a real referral link, or follow simple compliance guidance. It is not for under-18 users.",
  },
  {
    q: 'What happens when I get activated?',
    a: "Your tier moves from WAITLIST to ROOKIE, you receive a one-time starter credit bundle (Ad / Lead / Boost / Training / Game credits), your member tools unlock, and your own Pay-It-Forward queue starts moving. Progress beyond that depends on your actions.",
  },
];

const BeforeYouJoinFAQ = ({ variant = 'dark', defaultOpen = -1 }) => {
  const [openIndex, setOpenIndex] = useState(defaultOpen);
  const isDark = variant === 'dark';

  const palette = isDark
    ? {
        bg: 'transparent',
        cardBg: '#171f2b',
        cardBorder: '#1f2937',
        title: '#f8fafc',
        muted: '#94a3b8',
        accent: '#eab308',
      }
    : {
        bg: 'transparent',
        cardBg: '#ffffff',
        cardBorder: '#e2e8f0',
        title: '#0f172a',
        muted: '#64748b',
        accent: '#d97706',
      };

  return (
    <section
      className="py-10 md:py-14"
      style={{ backgroundColor: palette.bg }}
      data-testid="before-you-join-faq"
    >
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="mb-6 md:mb-8 text-center">
          <span
            className="inline-block text-xs uppercase tracking-wider mb-3 px-3 py-1 rounded-full"
            style={{
              color: palette.accent,
              backgroundColor: isDark ? 'rgba(234,179,8,0.12)' : 'rgba(217,119,6,0.10)',
              border: `1px solid ${palette.accent}40`,
            }}
          >
            Before You Join
          </span>
          <h2
            className="text-2xl md:text-3xl font-bold mb-2"
            style={{ color: palette.title }}
          >
            Read this first
          </h2>
          <p className="text-sm md:text-base" style={{ color: palette.muted }}>
            Plain answers. No hype. No income claims.
          </p>
        </div>

        <div className="space-y-2 md:space-y-3" data-testid="faq-list">
          {FAQS.map((item, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={item.q}
                className="rounded-xl overflow-hidden border transition-all"
                style={{
                  backgroundColor: palette.cardBg,
                  borderColor: isOpen ? `${palette.accent}55` : palette.cardBorder,
                }}
                data-testid={`faq-item-${idx}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : idx)}
                  className="w-full flex items-center justify-between gap-3 text-left px-4 md:px-5 py-4"
                  data-testid={`faq-toggle-${idx}`}
                  aria-expanded={isOpen}
                >
                  <span
                    className="font-medium text-sm md:text-base"
                    style={{ color: palette.title }}
                  >
                    {idx + 1}. {item.q}
                  </span>
                  <ChevronDown
                    className={`h-4 w-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    style={{ color: palette.accent }}
                  />
                </button>
                {isOpen && (
                  <div
                    className="px-4 md:px-5 pb-5 text-sm md:text-base leading-relaxed"
                    style={{ color: palette.muted }}
                    data-testid={`faq-answer-${idx}`}
                  >
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p
          className="text-xs text-center mt-6"
          style={{ color: palette.muted }}
        >
          BBC Quick List makes no income claims. Past or example results are not predictions.
        </p>
      </div>
    </section>
  );
};

export default BeforeYouJoinFAQ;
